import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { Illustration } from '@photosi/rn-components/illustration/Illustration';
import { observer } from 'mobx-react-lite';
import { InteractionManager, Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import braze from 'src/_shared/braze';
import { useStore } from 'src/contexts/StoreContext';
import { PickerComputerLoadingContent } from '../picker-computer/picker-computer-loading-content/PickerComputerLoadingContent';
import { SourcesEventType, sourcesMobileBottomNew, sourcesMobileBottomOld } from '../ModalUploadMultisource.constants';
import { ILLUSTRATION_NAMES } from '@photosi/rn-components/illustration/illustration.helper';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { MasonryGrid } from '@photosi/rn-components/masonry-grid/MasonryGrid';
import { MasonryItem } from '@photosi/rn-components/masonry-grid/MasonryItem';
import { PhotoCard } from '@photosi/rn-components/photo-card/PhotoCard';
import { useNavigation } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { styles } from './PickerGallery.style';
import { DynamicModalProps } from '../../DynamicModal.types';
import { ThumbnailImageModelInstance } from 'src/store/_models/thumbnailImage';
import { TextIconButton } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import {
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { PickerSourceButton } from '@photosi/rn-components/buttons/picker-source-button/PickerSourceButton';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { brand } from 'src/theme/colors';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { alert } from 'src/theme/colors';
import { getSourceType } from 'src/helpers/tracking.helper';

const UploadContent = observer(
  ({
    dynamicModalLayer: { uploadMultiSourceCallback, setSelectedItem, groupCode: groupCodeDynamic },
    selectedCard,
  }: DynamicModalProps) => {
    const {
      i18n,
      dynamicModal: { closeModal },
      toastStore,
      project: { activeProject, activeGroup, groupCode },
      photoDrawer,
    } = useStore();
    const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;
    const code = groupCodeDynamic ? groupCodeDynamic : activeGroup ? activeGroup?.code : groupCode;
    const groupTypes = activeProject?.groupTypes ?? [];
    const currentGroup = groupTypes.find(group => group.code === code);
    const labelButtonAdd = !currentGroup?.minItems
      ? selectedCard
        ? i18n.t('upload:choose-max', { photos: selectedCard?.replace('x', '') })
        : i18n.t('upload:choose')
      : i18n.t('upload:choose-with-minimum', { photos: currentGroup?.minItems });
    const sourcesMobile = newRoute ? sourcesMobileBottomNew : sourcesMobileBottomOld;

    const onPressHandler = async () => {
      const productImages: PickedImage[] = await pickImages({
        allowsMultipleSelection: true,
        i18n,
        errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
          if (areAllPhotosInvalid) {
            closeModal();
          }
          toastStore.showErrorToast({
            id: 'alert-uploading-photos-error-toast',
            message: errorMessage,
          });
        },
        newRoute: newRoute,
      });
      if (!!productImages.length) {
        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
          source_type: SourcesEventType.Gallery,
        });
        braze.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick);

        productImages.forEach(productImage => (productImage.source = SELECTED_MENU_ITEMS.Gallery));
        if (newRoute) {
          const autoselect = activeProject!.thumbnailImages.length == 0;
          productImages.forEach(file => (file.source = SELECTED_MENU_ITEMS.Gallery));
          try {
            await activeProject?.addImages(productImages).then(() => {
              if (autoselect) {
                activeProject.thumbnailImages.forEach(image => {
                  photoDrawer.addImageId(image.originalImageId);
                });
              }
            });
          } catch {}
        } else {
          uploadMultiSourceCallback({ files: productImages, sourceType: SourcesEventType.Gallery });
        }

        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
          n_photo: productImages.length,
          source_type: SourcesEventType.Gallery,
        });
      }
    };

    return (
      <>
        <View style={{ width: 100, height: 100 }}>
          <Illustration name={ILLUSTRATION_NAMES.Import} />
        </View>
        <View style={styles.details}>
          <View style={styles.textWrapper}>
            <TextLato style={styles.title}>{i18n.t('upload:title-mobile')}</TextLato>
            {!newRoute && <TextLato style={styles.text}>{i18n.t('upload:description-mobile')}</TextLato>}
          </View>

          <PrimaryButton
            style={styles.button}
            onPress={onPressHandler}
            testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE)}
            focusId={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE)}
            focusOrder={0}
            focusGroup={FocusGroups.MODAL}
            text={labelButtonAdd}
            textStyle={styles.buttonText}
            size={BUTTON_SIZES.REGULAR}
            type={BUTTON_TYPES.DEFAULT}
            layout={BUTTON_LAYOUTS.FILL}
          />
          <View style={styles.textWrapper}>
            <TextLato style={styles.title}>{i18n.t('upload:subtitle-mobile')}</TextLato>
          </View>
          {sourcesMobile.map((source, index) => {
            return (
              <PickerSourceButton
                style={styles.pickerButton}
                innerStyle={styles.pickerInnerButton}
                key={source}
                source={source}
                onPress={async () => {
                  setSelectedItem(source);
                  const source_type = getSourceType(source);
                  amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, { source_type });
                }}
                text={i18n.t(`upload:${source}`)}
                textStyle={styles.buttonTextPicker}
                focusId={modalTestID(source)}
                focusGroup={FocusGroups.MODAL}
                focusOrder={index + 1}
              />
            );
          })}
        </View>
      </>
    );
  },
);

export const PickerGallery = observer((props: DynamicModalProps) => {
  const {
    dynamicModalLayer: { isUploadingPhoto },
  } = props;
  const {
    project: { activeProject },
    i18n,
    dynamicModal: { openModal },
    data: { uploadError, fileListError },
  } = useStore();

  const images = activeProject?.thumbnailImages;
  const sortedImages = useMemo(
    () => images?.slice().sort((a, b) => b.fileAddedTimestamp - a.fileAddedTimestamp),
    [images, images?.length],
  );
  const onPressCard = (image: ThumbnailImageModelInstance) => {
    if (image.isSelected) {
      image.deselect();
    } else {
      image.select();
    }
  };

  const selectedThumbnails = activeProject!.selectedThumbnails.length;
  const ctaLabel =
    selectedThumbnails > 0
      ? i18n.t('modal-picker-my-photos:deselect-all')
      : i18n.t('modal-picker-my-photos:select-all');

  const [zoomData, setZoomData] = useState({ x: 0, y: 0, width: 0, height: 0, uri: undefined });
  const dropZoneRef = useRef(null);
  const imageRefs = useRef<Record<string, React.RefObject<View>>>({});

  sortedImages?.forEach(image => {
    if (!imageRefs.current[image.originalImageId]) {
      imageRefs.current[image.originalImageId] = createRef<View>();
    }
  });

  const scaleSV = useSharedValue(1);
  const opacitySV = useSharedValue(0);
  const translateXSV = useSharedValue(0);
  const translateYSV = useSharedValue(0);

  const onLongPress = (image: ThumbnailImageModelInstance) => {
    const ref = imageRefs.current[image.originalImageId];
    ref.current?.measureInWindow((x, y, width, height) => {
      dropZoneRef.current?.measureInWindow((parentX, parentY, parentWidth, parentHeight) => {
        let scaleFactor = 1;
        if (width > height) {
          scaleFactor = (parentWidth / width) * 0.8;
        } else {
          scaleFactor = (parentHeight / height) * 0.8;
        }
        const relativeX = x - parentX;
        const relativeY = y - parentY;
        const imageCenterX = relativeX + width / 2;
        const imageCenterY = relativeY + height / 2;
        const viewportCenterX = parentWidth / 2;
        const viewportCenterY = parentHeight / 2;
        const targetTranslateX = viewportCenterX - imageCenterX;
        const targetTranslateY = viewportCenterY - imageCenterY;
        opacitySV.value = withTiming(1, { duration: 200, easing: Easing.cubic });
        translateXSV.value = withSpring(targetTranslateX, { damping: 14 });
        translateYSV.value = withSpring(targetTranslateY, { damping: 14 });
        scaleSV.value = withSpring(scaleFactor, { damping: 10, stiffness: 120 });
        setZoomData({ x: relativeX, y: relativeY, width, height, uri: image.thumbnailUrl });
      });
    });
  };

  const closeZoom = () => {
    scaleSV.value = withSpring(1, { damping: 12 });
    translateXSV.value = withSpring(0, { damping: 12 });
    translateYSV.value = withSpring(0, { damping: 12 });
    opacitySV.value = withTiming(0, { duration: 150 }, () => {
      runOnJS(setZoomData)({ x: 0, y: 0, width: 0, height: 0, uri: undefined });
    });
  };

  const { width } = useWindowDimensions();

  const zoomedStyle = useAnimatedStyle(() => {
    if (!zoomData.uri) return {};
    return {
      position: 'absolute',
      left: zoomData.x,
      top: zoomData.y,
      opacity: opacitySV.value,
      transform: [{ translateX: translateXSV.value }, { translateY: translateYSV.value }, { scale: scaleSV.value }],
      width: zoomData.width,
      height: zoomData.height,
      outlineColor: 'rgba(0,0,0,0.5)',
      outlineStyle: 'solid',
      outlineWidth: width * 2,
    };
  });

  const onPressDelete = () => {
    if (!!activeProject?.selectedThumbnails?.length) {
      openModal(
        {
          dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteAll,
          thumbnailsToDelete: activeProject?.selectedThumbnails?.map(item => item.originalImageId),
        },
        true,
      );
    }
  };

  const isActiveDelete =
    !!activeProject?.selectedThumbnails?.length &&
    activeProject?.selectedThumbnails?.every(item => item.usageQuantity === 0);
  const scrollRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const prevLength = useRef(sortedImages?.length || 0);

  useEffect(() => {
    if (!sortedImages || scrollY === 0) return;
    const currentLength = sortedImages.length;
    if (currentLength !== prevLength.current) {
      InteractionManager.runAfterInteractions().then(() =>
        scrollRef.current?.scrollTo({
          y: scrollY,
          animated: false,
        }),
      );
    }
    prevLength.current = currentLength;
  }, [sortedImages?.length]);

  const fileErrorCount = fileListError.length;

  const openModalError = () => {
    openModal(
      {
        dynamicModalView: DYNAMIC_MODAL_VIEWS.ModalUploadError,
      },
      true,
    );
  };

  return (
    <View style={styles.container} ref={dropZoneRef}>
      {!!images?.length ? (
        <>
          <View style={styles.selectionTopBar}>
            <TextIconButton
              focusGroup={FocusGroups.MODAL}
              focusId={selectedThumbnails ? modalTestID(TestIDs.DESELECT_ALL) : modalTestID(TestIDs.SELECT_ALL)}
              testID={selectedThumbnails ? modalTestID(TestIDs.DESELECT_ALL) : modalTestID(TestIDs.SELECT_ALL)}
              focusOrder={10}
              text={ctaLabel}
              onPress={async () => {
                if (selectedThumbnails > 0) {
                  activeProject?.deselectAllThumbnailImages();
                } else {
                  activeProject?.selectAllThumbnailImages();
                }
              }}
              size={TEXT_ICON_BUTTON_SIZES.SMALL}
              type={TEXT_ICON_BUTTON_TYPES.NO_ICON}
              iconName={ICON_NAMES.AddFrame}
              textStyle={styles.textLink}
              accessible
              accessibilityRole="button"
              accessibilityLabel={ctaLabel}
            />
            {isActiveDelete && (
              <>
                <View style={{ height: '100%', width: 1, backgroundColor: brand.main.water }} />
                <TextIconButton
                  focusGroup={FocusGroups.MODAL}
                  focusId={modalTestID(TestIDs.DELETE_SELECTED)}
                  testID={modalTestID(TestIDs.DELETE_SELECTED)}
                  focusOrder={10}
                  text={i18n.t('group-action-bar:delete-selected')}
                  onPress={async () => onPressDelete()}
                  size={TEXT_ICON_BUTTON_SIZES.SMALL}
                  type={TEXT_ICON_BUTTON_TYPES.NO_ICON}
                  iconName={ICON_NAMES.AddFrame}
                  textStyle={styles.textLink}
                  accessible
                  accessibilityRole="button"
                  accessibilityLabel={i18n.t('group-action-bar:delete-selected')}
                />
              </>
            )}
          </View>
          <ScrollView
            style={{ flex: 1, height: '100%', width: '100%' }}
            contentContainerStyle={{ height: '100%', width: '100%' }}
            showsVerticalScrollIndicator={false}
            ref={scrollRef}
            onScroll={e => setScrollY(e.nativeEvent.contentOffset.y)}
          >
            {!isUploadingPhoto && uploadError && (
              <View style={styles.errorWrapper}>
                <View style={styles.errorInnerWrapper}>
                  <View style={styles.icon}>
                    <Icon name={ICON_NAMES.Alert} color={alert.alertRed} />
                  </View>
                  <TextLato style={styles.errorTitle}>{i18n.t('upload:upload-error-title')}</TextLato>
                </View>
                <TextLato style={styles.errorDescription}>
                  {i18n.t('upload:upload-error-description-head')}
                  <TextLato style={{ fontStyle: 'italic' }}>{fileListError?.slice(0, 3)?.join(', ')}</TextLato>
                  {fileErrorCount > 3
                    ? i18n.t('upload:upload-error-description-tail-counter', {
                        counter_file: fileErrorCount - 3,
                      })
                    : i18n.t('upload:upload-error-description-tail')}
                </TextLato>
                <View style={styles.errorInnerWrapper}>
                  <Pressable style={{ paddingLeft: 24, paddingTop: 8 }} onPress={openModalError}>
                    <TextLato style={styles.errorTextLink}>{i18n.t('upload:show-error-list')}</TextLato>
                  </Pressable>
                </View>
              </View>
            )}
            <View style={{ paddingHorizontal: 16, paddingTop: 8 }}>
              <MasonryGrid itemHeight={200} gap={6} outerGap={0}>
                {sortedImages?.map((image, index) => {
                  return (
                    <MasonryItem width={image.width} height={image.height} key={`grid-item-${image.originalImageId}`}>
                      <PhotoCard
                        image={image}
                        focusId={modalTestID(TestIDs.SELECT_PHOTO, index.toString())}
                        focusGroup={FocusGroups.MODAL}
                        focusOrder={index + 3}
                        testID={modalTestID(TestIDs.SELECT_PHOTO, index.toString())}
                        onPress={() => onPressCard(image)}
                        onLongPress={onLongPress}
                        closeZoom={closeZoom}
                        ref={imageRefs.current[image.originalImageId]}
                      />
                    </MasonryItem>
                  );
                })}
              </MasonryGrid>
            </View>
          </ScrollView>
          {zoomData.uri && (
            <Animated.Image source={zoomData.uri} style={zoomedStyle} pointerEvents="none" accessible={false} />
          )}
        </>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {!isUploadingPhoto && uploadError && (
            <View style={styles.errorWrapperTop}>
              <View style={styles.errorInnerWrapper}>
                <View style={styles.icon}>
                  <Icon name={ICON_NAMES.Alert} color={alert.alertRed} />
                </View>
                <TextLato style={styles.errorTitle}>{i18n.t('upload:upload-error-title')}</TextLato>
              </View>
              <TextLato style={styles.errorDescription}>
                {i18n.t('upload:upload-error-description-head')}
                <TextLato style={{ fontStyle: 'italic' }}>{fileListError?.slice(0, 3)?.join(', ')}</TextLato>
                {fileErrorCount > 3
                  ? i18n.t('upload:upload-error-description-tail-counter', {
                      counter_file: fileErrorCount - 3,
                    })
                  : i18n.t('upload:upload-error-description-tail')}
              </TextLato>
              <View style={styles.errorInnerWrapper}>
                <Pressable style={{ paddingLeft: 24, paddingTop: 8 }} onPress={openModalError}>
                  <TextLato style={styles.errorTextLink}>{i18n.t('upload:show-error-list')}</TextLato>
                </Pressable>
              </View>
            </View>
          )}
          {isUploadingPhoto ? <PickerComputerLoadingContent /> : <UploadContent {...props} />}
        </ScrollView>
      )}
    </View>
  );
});
